import { Helmet } from '@dr.pogodin/react-helmet';
import { useParams } from 'react-router-dom';
import { Api } from 'src/api';
import {
  PageError,
  PageLoading,
  PageWrap,
  Stack,
  Text,
  TimerModule,
} from 'src/components';
import { Color, Style } from 'src/constants';
import { useTimerChange } from 'src/contexts';
import { useMyUserClient } from 'src/hooks';
import { Model } from 'src/models';

import {
  AvailableTicketsSection,
  DailyTasksSection,
  DashboardTabs,
  TasksUserSelect,
  TimeLoggedBanner,
} from '../components';

type Props = {
  userId: string;
};

function TasksDashboard({ userId }: Props) {
  const myUser = useMyUserClient();
  const isSelf = userId === myUser.id;
  const userIri = `/users/${userId}`;

  const apiTasks = Api.DailyTask.useReadCollection({ user: userIri });
  const apiAgencyUsers = Api.User.useReadCollectionAgency();

  // Stopping a timer banks minutes against the day, which these totals show.
  useTimerChange(apiTasks.invalidate);

  if (apiTasks.error) return <PageError {...apiTasks} />;
  if (apiAgencyUsers.error) return <PageError {...apiAgencyUsers} />;
  const tasks = apiTasks.data?.['hydra:member'];
  const agencyUsers = apiAgencyUsers.data?.['hydra:member'];
  if (!tasks || !agencyUsers) return <PageLoading />;

  const users = Model.User.getAgencySorted(agencyUsers);
  const user = users.find((candidate) => candidate.id === userId);
  if (!user) throw new Error(`TasksDashboard: unknown agency user ${userId}`);

  const completed = tasks.filter((task) => task.isCompleted);
  const assigned = tasks.filter((task) => !task.isCompleted);
  const minutesToday = tasks.reduce(
    (sum, task) => sum + task.timeLoggedToday,
    0
  );

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="small">
      <Helmet>
        <title>
          {isSelf
            ? 'Daily Tasks'
            : `Daily Tasks | ${Model.User.getFullName(user)}`}
        </title>
      </Helmet>

      <Stack padH={1.5} padV={1.5} gap={1.5}>
        <DashboardTabs selected="tasks" userId={userId} />

        <Stack
          style={[Style.row, Style.alignCenter, Style.spaceBetween, Style.wrap]}
          gap={1}
        >
          <Text typeface="headline" as="h1">
            Daily Tasks
          </Text>

          <TasksUserSelect users={users} selectedUser={user} />
        </Stack>

        {/* A timer only ever belongs to whoever is signed in, so colleagues'
            dashboards keep the plain summary of their day. */}
        {isSelf ? (
          <TimerModule minutesToday={minutesToday} />
        ) : (
          <TimeLoggedBanner minutes={minutesToday} />
        )}

        {completed.length > 0 && (
          <DailyTasksSection
            title="Completed"
            tasks={completed}
            userId={userId}
            emptyMessage="Nothing finished yet today"
            collapsible
            isCompleted
          />
        )}

        <DailyTasksSection
          title={isSelf ? 'Assigned to you' : `Assigned to ${user.firstName}`}
          tasks={assigned}
          userId={userId}
          emptyMessage="Nothing assigned right now"
        />

        <AvailableTicketsSection
          userIri={userIri}
          taker={isSelf ? myUser : undefined}
        />
      </Stack>
    </PageWrap>
  );
}

export default function TasksDashboardPage() {
  const { userId } = useParams<{ userId: string }>();
  if (!userId) throw new Error('TasksDashboardPage: missing userId');

  return <TasksDashboard userId={userId} />;
}
